import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/29416913/pexels-photo-29416913/free-photo-of-charming-cabin-in-podgaric-with-fall-foliage.jpeg?auto=compress&cs=tinysrgb&w=1920";

const lines = [
    <span key="l1">Votre propriété,</span>,
    <span key="l2">gérée comme</span>,
    <span key="l3" className="italic text-[#8A7563]">un palace.</span>,
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

    return (
        <section id="hero" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img src={HERO_IMG} alt="Chalet chaleureux en bois entouré d'arbres aux couleurs d'automne" className="h-[120%] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#FAF8F5] via-[#FAF8F5]/70 to-[#FAF8F5]/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5] via-transparent to-[#FAF8F5]/50" />
            </motion.div>
            <div className="gold-spotlight absolute inset-0" />

            <motion.div style={{ opacity: fade }} className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 pt-36 lg:grid-cols-[1.2fr_0.8fr]">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="eyebrow mb-8 flex items-center gap-3"
                    >
                        <span className="inline-block h-px w-10 bg-[#8A7563]" />
                        Conciergerie Airbnb Premium &amp; Yield Management
                    </motion.p>

                    <h1 className="font-display text-4xl font-semibold leading-[1.08] sm:text-5xl lg:text-6xl">
                        {lines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-8 max-w-xl text-base font-medium leading-relaxed text-[#5A4F44] md:text-lg"
                    >
                        Photos professionnelles, ménage hôtelier, annonce optimisée, voyageurs
                        sélectionnés : nous gérons l'intégralité de votre location. En échange,
                        une simple commission sur vos réservations. Rien d'autre.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-estimate-button"
                            onClick={() => scrollToSection("#simulateur")}
                            className="group flex items-center gap-3 rounded-full bg-[#8A7563] px-7 py-3.5 text-sm font-semibold text-[#FAF8F5] transition-all duration-300 hover:bg-[#6E5D4E] hover:shadow-[0_0_32px_rgba(138,117,99,0.35)]"
                        >
                            Estimer mes revenus
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-services-button"
                            onClick={() => scrollToSection("#services")}
                            className="rounded-full border border-[#2B2622]/15 px-7 py-3.5 text-sm font-medium text-[#2B2622] transition-all duration-300 hover:border-[#8A7563]/60 hover:text-[#8A7563]"
                        >
                            Découvrir nos services
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="mt-14 flex flex-wrap items-center gap-8 border-t border-[#2B2622]/10 pt-8"
                    >
                        {[
                            ["4,98/5", "note moyenne voyageurs"],
                            ["+32 %", "de revenus en moyenne"],
                            ["24/7", "conciergerie dédiée"],
                        ].map(([v, l]) => (
                            <div key={l}>
                                <p className="font-display text-2xl font-semibold text-[#8A7563]">{v}</p>
                                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#8A7F72]">{l}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
}
